import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Button, BUTTON_SIZE, BUTTON_TYPES, LoaderSVG, Modal } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ResourceAuthorization } from '@src/quince-site-common/src/services/trustAndSafetyService/trustAndSafety.types'
import trustAndSafetyService from '@src/quince-site-common/src/services/trustAndSafetyService/trustAndSafetyService.service'
import { getDeviceIdFromLocalStorage, setDeviceIdInLocalStorage } from '@src/quince-site-common/src/utils/getlb-user-id'
import { isHttpClientError } from '@src/quince-site-common/src/utils/HttpClient'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { Input } from '@src/quince-site-integrated/src/components/FormElements'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { ERROR_CODES, getFmoTrackingId, RESEND_TIMER } from '../utils'

import styles from './otpModal.module.scss'
import { ResourceEntityType } from './types'

interface Props {
  open: boolean
  verificationResource: string
  onClose: () => void
  otpRequestId: string | null
  setOtpRequestId: (otpRequestId: string | null) => void
  onSuccess: (otpRequestId: string) => void
  setShowOtpModal: (showOtpModal: boolean) => void
  onModalOpen?: () => void
  onError: (showNoResultError: boolean) => void
  resetErrorState: () => void
}
export default function OtpModal(props: Props) {
  const {
    open,
    verificationResource,
    onClose,
    otpRequestId,
    setOtpRequestId,
    onSuccess,
    setShowOtpModal,
    onModalOpen,
    onError,
    resetErrorState,
  } = props
  const [loading, setLoading] = useState(true)
  const [otp, setOtp] = useState('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [resendEnabled, setResendEnabled] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [resendTimer, setResendTimer] = useState(RESEND_TIMER)
  const [hideModal, setHideModal] = useState(true)
  const { t } = useTranslation('common')

  const { user } = useCurrentUser()

  const { customerMetadata } = user ?? {}

  const inputRef = React.useRef<HTMLInputElement>(null)
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startResendTimer = useCallback(() => {
    setResendTimer(RESEND_TIMER)
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
    }
    timerIntervalRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current)
            timerIntervalRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  const extractOtpRequestId = (resourceAuthorizations: ResourceAuthorization[]) => {
    const authorization = resourceAuthorizations?.find(
      (auth: any) =>
        auth?.resourceType === ResourceEntityType.CUSTOMER && auth?.userAction === ResourceEntityType.FIND_ORDER,
    )
    return authorization?.authenticationMetaData ?? null
  }

  const checkIfAlreadyAuthorized = useCallback((resourceAuthorizations: ResourceAuthorization[]) => {
    const authorization = resourceAuthorizations?.find(
      (auth: any) =>
        auth?.resourceType === ResourceEntityType.CUSTOMER &&
        auth?.userAction === ResourceEntityType.FIND_ORDER &&
        auth?.authorized,
    )
    return authorization ? true : false
  }, [])

  const generateOtp = useCallback(async () => {
    resetErrorState?.()
    try {
      trackEvent(TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK, {
        properties: {
          actionName: TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK,
          actionData: `Initiate OTP Check`,
          verificationResource,
          ...getFmoTrackingId(customerMetadata),
        },
      })
      let deviceId = getDeviceIdFromLocalStorage()
      if (!deviceId) {
        deviceId = await trustAndSafetyService.generateDeviceId(verificationResource)
        setDeviceIdInLocalStorage(deviceId)
      }
      const data = await trustAndSafetyService.generateOtp(verificationResource)
      const isAlreadyAuthorized = checkIfAlreadyAuthorized(data?.resourceAuthorizations)
      const authenticationMetaData = extractOtpRequestId(data?.resourceAuthorizations)
      setOtpRequestId(authenticationMetaData?.otpRequestId ?? null)
      setResendEnabled(authenticationMetaData?.openOTPRequest?.canResend ?? false)
      // Hide modal if already authorized user
      if (isAlreadyAuthorized) {
        onSuccess(authenticationMetaData?.otpRequestId ?? '')
      } else {
        onModalOpen?.()
        setHideModal(false)
      }
    } catch (error: any) {
      onModalOpen?.()
      const _isHttpClientError = isHttpClientError(error)
      const errCode = _isHttpClientError ? error?.response?.data?.code : null

      if (errCode === ERROR_CODES.CUSTOMER_NOT_FOUND) {
        onError?.(false)
      } else {
        onError?.(true)
      }
      captureException(new Error('Failed to generate otp'), {
        feature: FEATURES.FIND_MY_ORDER,
        module: MODULES.PRODUCT_CARD,
        extraInfo: {
          // @i18n-ignore
          error: error,
          capturedFrom: 'generateOtp in OtpModal.tsx',
        },
      })
      trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
        apiName: `generateOtp`,
        status: `failure`,
        context: {
          error: error,
          resourceType: 'CUSTOMER',
          userAction: 'FIND_ORDER',
          authenticationMode: 'OTP',
        },
      })
      setShowOtpModal(false)
      setOtpRequestId(null)
    } finally {
      setLoading(false)
    }
  }, [t, verificationResource, setOtpRequestId, setShowOtpModal])

  function onOtpChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.replace(/[^0-9]/g, '')
    setOtp(value)
    setErrorMessage('')
  }

  const resendOtp = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')
      setOtp('')
      let deviceId = getDeviceIdFromLocalStorage()
      trackEvent(TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT, {
        properties: {
          actionName: TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT,
          actionData: `OTP Resend Attempt`,
          verificationResource,
          ...getFmoTrackingId(customerMetadata),
        },
      })
      if (!deviceId) {
        deviceId = await trustAndSafetyService.generateDeviceId(verificationResource)
        setDeviceIdInLocalStorage(deviceId)
      }
      const data = await trustAndSafetyService.resendOtp(verificationResource)
      const authenticationMetaData = extractOtpRequestId(data.resourceAuthorizations)
      setOtpRequestId(authenticationMetaData?.otpRequestId ?? null)
      setResendEnabled(authenticationMetaData?.openOTPRequest?.canResend ?? false)
    } catch (error) {
      const _isHttpClientError = isHttpClientError(error)
      const errCode = _isHttpClientError ? error?.response?.data?.code : null

      if (errCode === ERROR_CODES.CUSTOMER_NOT_FOUND) {
        onError?.(false)
      } else {
        onError?.(true)
      }
      captureException(new Error('Failed to generate otp'), {
        feature: FEATURES.FIND_MY_ORDER,
        module: MODULES.PRODUCT_CARD,
        extraInfo: {
          // @i18n-ignore
          error: error,
          capturedFrom: 'resendOtp in OtpModal.tsx',
        },
      })
      trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
        apiName: `resendOtp`,
        status: `failure`,
        context: {
          error: error,
          resourceType: 'CUSTOMER',
          userAction: 'FIND_ORDER',
          authenticationMode: 'OTP',
        },
      })
      setShowOtpModal(false)
      setOtpRequestId(null)
    } finally {
      setLoading(false)
    }
  }, [verificationResource, setOtpRequestId, t, otp, setShowOtpModal])

  const submitOtp = useCallback(async () => {
    if (!otpRequestId || submitting) return
    try {
      setSubmitting(true)
      trackEvent(TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT, {
        properties: {
          actionName: TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT,
          actionData: `OTP Validate Attempt`,
          verificationResource,
          ...getFmoTrackingId(customerMetadata),
        },
      })
      const data = await trustAndSafetyService.submitOtp(otp, otpRequestId, verificationResource)
      const isValid = data?.resourceAuthorizations?.find(
        (auth: ResourceAuthorization) =>
          auth?.resourceType === ResourceEntityType.CUSTOMER && auth?.userAction === ResourceEntityType.FIND_ORDER,
      )?.authorized
      if (isValid) {
        onSuccess(otpRequestId)
      } else {
        setErrorMessage(t('invalid_otp'))
        return
      }
    } catch (error) {
      const fallbackErrorMessage = t('failed_to_validate_otp')
      const _isHttpClientError = isHttpClientError(error)
      const errorCode = _isHttpClientError ? error?.response?.data?.errorResponse?.code : null
      const errMsg = _isHttpClientError ? error?.response?.data?.message || fallbackErrorMessage : fallbackErrorMessage

      trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
        apiName: `submitOtp`,
        status: `failure`,
        context: {
          error: error,
          resourceType: 'CUSTOMER',
          userAction: 'FIND_ORDER',
          authenticationMode: 'OTP',
        },
      })

      if (errorCode !== ERROR_CODES.INVALID_OTP) {
        if (errorCode === ERROR_CODES.CUSTOMER_NOT_FOUND) {
          onError?.(false)
        } else {
          onError?.(true)
        }
        onClose()
      }
      setErrorMessage(errMsg)
    } finally {
      setSubmitting(false)
    }
  }, [otp, otpRequestId, onSuccess, submitting, t, verificationResource, onClose])

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setOtp('')
      setErrorMessage('')
      setLoading(true)
      setResendTimer(RESEND_TIMER)
      void generateOtp()
    }

    return () => {
      // Clean up timer interval on unmount
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [generateOtp, open])

  // Start resend timer and focus input when loading finishes
  useEffect(() => {
    let innerTimer: ReturnType<typeof setTimeout>
    let timer: ReturnType<typeof setTimeout>

    if (!loading && open) {
      startResendTimer()
      timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          innerTimer = setTimeout(() => {
            inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }, 300)
        }
      }, 100)
    }

    return () => {
      clearTimeout(timer)
      clearTimeout(innerTimer)
    }
  }, [loading, open, startResendTimer])

  return (
    <Modal
      open={open && !hideModal}
      onClose={onClose}
      showCloseButton={true}
      className={styles.modal}
      responsiveBottomSheet={true}
    >
      {loading ? (
        <div className={styles.loaderContainer}>
          <LoaderSVG className={styles.loader} />
        </div>
      ) : (
        <div>
          <h2 className={styles.modalHeader}> {t('verification_required')} </h2>
          <p className={styles.modalDescription}>
            {t('verification_description', { type: 'email' })}
            <span className={styles.emailHighlight}>{verificationResource}</span>
          </p>
          <div className={styles.otpInput}>
            <Input
              inputRef={inputRef}
              type="text"
              placeholder={t('enter_code')}
              value={otp}
              onChange={onOtpChange}
              disabled={loading || submitting}
              maxLength={4}
            />
            {errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}
          </div>
          <Button
            className={styles.displayButtonWrapper}
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            disabled={otp.length !== 4}
            round={true}
            onClick={() => void submitOtp()}
            showLoader={loading || submitting}
          >
            {t('submit')}
          </Button>
          {resendEnabled && (
            <div className={styles.resendContainer}>
              {t('didnt_get_the_code_v1')}
              <span className={styles.resendEnabled} onClick={() => resendTimer === 0 && void resendOtp()}>
                {t('resend')} {resendTimer > 0 ? `(${resendTimer}s)` : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
